import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useRefundPoints } from '../../../../../hooks/refundPoints/useRefundPoints'

import Loading from '../../../../loading/Loading'
import { Card } from '../../../../card/Card'
import KPI from '../../../../ui/kpi/KPI'
import { ListRow } from '../../../../listItem/listRow/ListRow'
import { ListCard } from '../../../../listItem/listCard/ListCard'
import { SimpleTable } from '../../../../listing/simpleTable/SimpleTable'

import { useApp } from '../../../../../contexts/AppContext'
import { BannerKind } from '../../../../../types/contexts/AppContext'
import { ListCardStripe } from '../../../../../types/listing'
import {
  RefundPoint,
  RefundPointStatus,
  RefundPointSummary,
  RefundPointUsageStatus,
} from '../../../../../types/refundPoint/refundPoint'
import { formatPoints, timeOf } from '../../../../../utils/booking/booking'
import { toDisplayLocalDate, toDisplayOnlyDate } from '../../../../../utils/dateUtils'

const STATUS: Record<RefundPointStatus, { badge: string; stripe: ListCardStripe }> = {
  [RefundPointStatus.AVAILABLE]: { badge: 'badge-success', stripe: 'success' },
  [RefundPointStatus.USED]: { badge: 'badge-secondary', stripe: 'neutral' },
  [RefundPointStatus.EXPIRED]: { badge: 'badge-secondary', stripe: 'neutral' },
}

/**
 * Refund points: the balance, then every entry (one per cancelled paid booking) with what is left, its expiry and where
 * it was spent.
 */
export const RefundPointList = () => {
  const { t, i18n } = useTranslation('account')
  const { showBanner } = useApp()
  const refundPoints = useRefundPoints()
  const [points, setPoints] = useState<RefundPoint[] | null>(null)
  const [summary, setSummary] = useState<RefundPointSummary | null>(null)

  useEffect(() => {
    Promise.all([refundPoints.mine(), refundPoints.summary()])
      .then(([list, balance]) => {
        setPoints(list ?? [])
        setSummary(balance ?? null)
      })
      .catch(caught => showBanner({ kind: BannerKind.ERR, msg: (caught as Error).message }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (points === null) {
    return <Loading variant="inline" />
  }

  const status = (point: RefundPoint) => (
    <span className={`badge ${STATUS[point.status].badge}`}>
      {t(`account.billing.refunds.status.${point.status.toLowerCase()}`)}
    </span>
  )

  const origin = (point: RefundPoint) =>
    [
      point.courtName,
      point.bookingStartTime &&
        `${toDisplayOnlyDate(point.bookingStartTime, i18n.language)} ${timeOf(point.bookingStartTime)}`,
    ]
      .filter(Boolean)
      .join(' · ')

  const reason = (point: RefundPoint) =>
    point.cancelReason ? t(`account.billing.refunds.reason.${point.cancelReason.toLowerCase()}`) : '—'

  /** Points spent for good on later purchases (reserved ones are still waiting for their payment). */
  const used = (point: RefundPoint) =>
    point.usages
      .filter(usage => usage.status !== RefundPointUsageStatus.RELEASED)
      .reduce((sum, usage) => sum + usage.amount, 0)

  const amounts = (point: RefundPoint) =>
    t('account.billing.refunds.amounts', {
      remaining: formatPoints(point.remainingAmount),
      amount: formatPoints(point.amount),
    })

  const expires = (point: RefundPoint) => toDisplayLocalDate(point.expiresAt, i18n.language)

  return (
    <>
      <div className="row g-3 mb-4 align-items-stretch">
        <Card tone="accent" className="col-12 col-md-6 col-xl-4">
          <KPI
            tone="accent"
            label={t('account.billing.refunds.available')}
            value={formatPoints(summary?.available)}
            sub={t('account.billing.refunds.available-sub')}
          />
        </Card>
        {summary?.nextExpiresAt && (
          <Card className="col-12 col-md-6 col-xl-4">
            <KPI
              label={t('account.billing.refunds.next-expiry')}
              value={formatPoints(summary.nextExpiryAmount)}
              sub={t('account.billing.refunds.next-expiry-sub', {
                date: toDisplayLocalDate(summary.nextExpiresAt, i18n.language),
              })}
            />
          </Card>
        )}
      </div>

      <h3 className="mb-3">{t('account.billing.refunds.title')}</h3>
      {points.length === 0 ? (
        <div className="empty muted">{t('account.billing.refunds.empty')}</div>
      ) : (
        <>
          <SimpleTable
            columns={[
              { key: 'issued', label: t('account.billing.refunds.header.issued'), align: 'center' },
              { key: 'booking', label: t('account.billing.refunds.header.booking'), align: 'center' },
              { key: 'reason', label: t('account.billing.refunds.header.reason'), align: 'center' },
              { key: 'points', label: t('account.billing.refunds.header.points'), align: 'center' },
              { key: 'used', label: t('account.billing.refunds.header.used'), align: 'center' },
              { key: 'expires', label: t('account.billing.refunds.header.expires'), align: 'center' },
              { key: 'status', label: t('account.billing.refunds.header.status'), align: 'center' },
            ]}
          >
            {points.map(point => (
              <ListRow
                key={point.id}
                cells={[
                  {
                    key: 'issued',
                    className: 'mono text-center',
                    content: toDisplayLocalDate(point.createdAt, i18n.language),
                  },
                  { key: 'booking', className: 'text-center', content: origin(point) },
                  { key: 'reason', className: 'text-center', content: reason(point) },
                  { key: 'points', className: 'mono text-center fw-semibold', content: amounts(point) },
                  { key: 'used', className: 'mono text-center', content: formatPoints(used(point)) },
                  { key: 'expires', className: 'mono text-center', content: expires(point) },
                  { key: 'status', className: 'text-center', content: status(point) },
                ]}
              />
            ))}
          </SimpleTable>
          <div className="row g-3 d-xxl-none">
            {points.map(point => (
              <div key={point.id} className="col-12 col-xl-6">
                <ListCard
                  stripe={STATUS[point.status].stripe}
                  title={amounts(point)}
                  subtitle={origin(point)}
                  status={status(point)}
                  muted={point.status !== RefundPointStatus.AVAILABLE}
                  fields={[
                    { key: 'reason', label: t('account.billing.refunds.header.reason'), value: reason(point), mono: false },
                    { key: 'used', label: t('account.billing.refunds.header.used'), value: formatPoints(used(point)) },
                    { key: 'expires', label: t('account.billing.refunds.header.expires'), value: expires(point) },
                  ]}
                />
              </div>
            ))}
          </div>
        </>
      )}
    </>
  )
}
